<template>
  <div>
    <DetailModal :data="detailModalState" @close-modal="closeDetailModal" />
    <div>
      <DataTable
        v-model:selection="selectedProduct"
        :value="cleanedItems"
        data-key="id"
        :pt="getDataTablePt()"
        @row-click="onRowClick"
      >
        <Column selection-mode="multiple" class="border-b border-gray-275" :pt="getColumnPt()" />
        <Column
          v-for="(header, colIndex) in headers"
          :key="colIndex"
          :field="header"
          :header="header"
          class="border-b border-gray-275"
          :pt="getHeaderPt()"
        />
      </DataTable>
      <div ref="sentinel" class="w-full p-4" />
    </div>
    <div class="flex items-center justify-center" data-testid="results">
      <p v-if="props.isLastPage" class="text-sm font-light pt-4">
        End of Results
      </p>
      <ais-stats v-else v-slot="{ nbHits }">
        <p class="text-sm font-light pt-4">Showing {{ props.results.length }} of {{ nbHits }}</p>
      </ais-stats>
    </div>
  </div>
  <Spinner v-if="loading" class="pt-4" outer-classes="" data-testid="spinner" />
</template>

<script setup>
import { useRouter } from "vue-router";
import { ref, computed, defineProps } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { useIntersectionObserver } from "@vueuse/core";
import { debounce } from "lodash";
import Spinner from "@/components/helpers/Spinner.vue";
import { getColumnPt, getHeaderPt, getDataTablePt } from "./style.js";
import DetailModal from "@/components/modals/DetailModal";
import { isLibraryItemOwner, hasProgramsWritePermission } from "@/composables/authorization.js";
import { AisStats } from "vue-instantsearch/vue3/es";
import { PROGRAM_MANAGER_V2_ROUTES } from "@/constants.js";

const router = useRouter();

const props = defineProps({
  results: { type: Array, required: true },
  refineNext: {
    type: Function,
  },
  isLastPage: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: "",
  },
});

const selectedProduct = ref([]);
const sentinel = ref(null);
const loading = computed(() => props.status == "loading" || props.status == "stalled");

useIntersectionObserver(sentinel, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    refineNextWithDebounce();
  }
});

/**
 * Debounced to prevent network storm from unresolved responses from Algolia
 */
const refineNextWithDebounce = debounce(() => {
  if (!props.isLastPage) {
    props.refineNext();
  }
}, 300);

const hasEditPermissions = (programSessionId) => {
  const isOwner = isLibraryItemOwner(programSessionId);
  return isOwner && hasProgramsWritePermission();
};

const detailModalState = ref({
  id: undefined,
  state: false,
});

const openDetailModal = (programSessionId) => {
  detailModalState.value = { id: programSessionId, state: true };
};

const closeDetailModal = () => {
  detailModalState.value = { id: undefined, state: false };
};

const onRowClick = (event) => {
  const clicked = event.data;
  if (hasEditPermissions(clicked.owner_entity_id)) {
    router.push({
      name: PROGRAM_MANAGER_V2_ROUTES.EDIT_SESSIONS,
      params: { programSessionId: clicked.id },
    });
  } else {
    openDetailModal(clicked.id);
  }
};

const formatDate = (dateInput) => {
  if (!dateInput) {
    return "";
  }
  const date = new Date(dateInput);
  return date.getTime() ? date.toLocaleDateString() : "";
};

const cleanedItems = computed(() =>
  props.results.map(({ session }) => ({
    id: session.id,
    owner_entity_id: session.owner_entity_id,
    "Program Session Name": session.name,
    Status: session.status?.name || "",
    "Term(s)": session.terms.map(({ name }) => name).join(", "),
    "Last Modified": formatDate(session.updated_at),
  }))
);

const headers = computed(() => {
  if (cleanedItems.value.length == 0) {
    return [];
  }
  const headingFields = Object.keys(cleanedItems.value[0]);

  return headingFields.filter((field) => field !== "id" && field !== "owner_entity_id");
});
</script>
